/**
 * feature-config flag names owned by subscription & payments
 *
 * please consider removing this file and adding it to an appropriate place in the
 * feature code (e.g. an entrypoint, or a package owned by the feature team), or
 * passing flag names directly as a string!
 *
 * by separating it out, we decouple the feature code from the feature config packages
 * and avoid the need to update this package when flags are added or removed.
 */

export const SUBSCRIPTION_AND_PAYMENTS_FLAG_NAMES = {
  CAMPAIGNS_TRIAL_SIGNUP_PROVISIONING:
    'ff-sub-229-campaigns-trial-signup-provisioning',
  // SUB-484: attaches a paid Campaign Hub Core subscription at create time
  // when the workspace has hosted-hub headroom, falling back to the free
  // Starter plan otherwise. Evaluated once, inside
  // `campaignHubProvisioningStrategy.reserveSlot` — everything downstream
  // (release, provisioning) reads the reservation it produced rather than
  // re-checking the flag.
  CAMPAIGN_HUB_CORE_PROVISIONING: 'ff-sub-484-campaign-hub-core-provisioning',
  COUPON_API_AUTH_ROLLOUT: 'ff-sub-587-coupon-api-auth-rollout',
  BILLING_AO_OVERAGES: 'ff-pymt-4362-ao-overages',
  BILLING_RECONCILE_SITE_CMS_LIMITS: 'ff-pymt-5931-reconcile-site-cms-limits',
  BILLING_BEFORE_YOU_GO: 'ff-pymt-4934-before-you-go',
  BILLING_BLOCK_SELFSERVE: 'ff-pymt-2567-ss-block',
  BILLING_CHECKOUT_IN_DESIGNER: 'ff-pymt-5119-checkout-in-designer',
  EU_BLOCK_SELF_SERVE_BILLING: 'ff-gov-3025-eu-block-self-serve-billing',
  BILLING_DISABLE_AUTO_UPGRADE_SCHEDULING:
    'ff-pymt-5522-disable-auto-upgrade-scheduling',
  BILLING_SUBSCRIPTION_DAL_USES_STIGG:
    'ff-pymt-3101-billing-sub-dal-uses-stigg',
  MULTI_SKU_CHECKOUT_WORKSPACES: 'ff-sub-303-multi-sku-checkout-workspaces',
  MULTI_SKU_CHECKOUT_WORKSPACE_UPDATES:
    'ff-sub-392-multi-sku-checkout-workspace-updates',
  MULTI_SKU_CHECKOUT_WORKSPACE_ADDONS:
    'ff-sub-395-multi-sku-checkout-workspace-addons',
  MULTI_SKU_CHECKOUT_WORKSPACE_DOWNGRADES:
    'ff-sub-402-multi-sku-checkout-workspace-downgrades',
  MULTI_SKU_CHECKOUT_WORKSPACE_INTERVAL_SWITCH:
    'ff-sub-927-multi-sku-checkout-workspace-interval-switch',
  BILLING_SYNC_WITH_STIGG: 'ff-bill-2255-billing-sync-with-stigg',
  BILLING_ENABLE_MARKETPLACE_ANROK: 'ff-pymt-3716-enable-marketplace-anrok',
  DASHBOARD_PAGINATION_WORKSPACE_SITES_HANDLER:
    'ff-pymt-4880-add-getworkspacesiteshandler',
  ENTITLEMENT_USAGE_NOTIFICATIONS: 'ff-grow-entitlement-usage-notifications',
  AI_CREDITS: 'ff-apai-1730-credits',
  AI_CREDITS_USAGE_GROUPBY_DIMENSIONS:
    'ff-pymt-6084-ai-credits-usage-groupby-dimensions',
  AI_CREDITS_LOW_BALANCE_ALERT: 'ff-pymt-5938-ai-credits-low-balance-alert',
  AI_CREDITS_USAGE_EMAIL_NOTIFICATIONS:
    'ff-pymt-5872-ai-credits-usage-email-notifications',
  // SUB-492: on Designer disconnect, abort the page-settings model call and
  // don't charge for it. Off is byte-for-byte the pre-SUB-492 behavior.
  AI_CREDITS_ABORT_PAGE_SETTINGS_ON_DISCONNECT:
    'ff-sub-492-abort-page-settings-on-disconnect',
  // SUB-754: when the Stigg credit-balance read fails with a status in
  // CREDIT_BALANCE_PROVIDER_UNAVAILABLE_STATUSES, fail open in
  // getAiCreditLimitState so AI features keep running and defer the credit
  // report to retryReportEvent. Off rethrows the balance error.
  AI_CREDITS_BALANCE_READ_FAIL_OPEN:
    'ff-sub-754-ai-credits-balance-read-fail-open',
  DASHBOARD_PAGINATION_ACCESSIBLE_SITES:
    'ff-pymt-4954-fix-pagination-search-flicker',
  ASSETS_USAGE_DEEP_LINK: 'ff-pymt-4576-asset-link',
  SITE_PLAN_CANCELLATION: 'ff-epd-915-site-plan-cancellation',
  WORKSPACE_IMMEDIATE_CANCELLATION:
    'ff-epd-1344-workspace-immediate-cancellation',
  BILLING_ENABLE_RECONCILIATION_FOR_PREV_BILLING_MONTH:
    'ff-pymt-4797-enable-reconciliation-for-prev-billing-month',
  BILLING_ENABLE_POST_RECONCILIATION_DELAY:
    'ff-pymt-4797-enable-post-reconciliation-delay',
  /*
   * Master switch for the temporary "enterprise plan name" override in the
   * billing-subscriptions DAL. When ON, updateSubscriptionsWithEnterprisePlanNames
   * (and the legacy path in findBillingSubscriptionsForSites) rewrites a site or
   * workspace subscription's plan id to its legacy Enterprise-equivalent name
   * (e.g. SITE_PLAN_NAMES.LEGACY_ENTERPRISE_V1) from the workspace's beta features —
   * the bridge used to label enterprise workspaces (often ones with many sites)
   * until enterprise plans exist natively in the billing service.
   *
   * The override runs a beta-features lookup that is expensive for some customers,
   * so this flag can disable it entirely or be scoped to specific workspaces via
   * Statsig overrides. When OFF the impact is cosmetic — dashboards show the
   * underlying (non-enterprise) plan name. Tracked in PYMT-4182.
   */
  BILLING_ENABLE_ENTERPRISE_SITE_NAMES: 'ff-pymt-4182-ent-sitenames',
  BILLING_UPCOMING_WORKSPACE_RENEWAL_EMAIL:
    'ff-pymt-5473-upcoming-workspace-renewal-email',
  DASHBOARD_PAGINATE_TEAMS_PAGE: 'ff-pymt-4919-dashboard-teams-paginate-sites',
  DASHBOARD_PRESERVE_FOLDER_ON_DUPLICATION:
    'ff-pymt-4961-dashboard-folder-retain',
  BILLING_ENABLE_CUBE_CHECK: 'ff-pymt-4877-cube-check',
  ENABLE_DASHBOARD_PRELOAD: 'ff-enable-server-side-preload',
  BILLING_EXTRA_STRIPE_METADATA: 'ff-pymt-4559-additional-stripe-metadata',
  BILLING_ENABLE_BLOCK_EMPTY_USER_AGENT_TRAFFIC_TOGGLE:
    'ff-pymt-4791-block-empty-user-agent-traffic',
  SITE_CLIENT_MANAGEMENT: 'ff-pymt-4944-site-client-management',
  BILLING_ENABLE_SELF_PROVISIONING_ENTERPRISE_SITE_PLAN:
    'ff-pymt-5235-allow-self-provisioning-ent-site-plan',
  DISPLAY_LEGACY_EDITOR_DEPRECATION_BANNER:
    'ff-pymt-5324-product-notifs-to-sps',
  IMPROVE_INVITE_MANAGEMENT: 'ff-pymt-5303-improve-invite-management',
  HIDE_LEGACY_EDITORS: 'ff-pymt-5318-hide-legacy-editor',
  HIDE_ADD_LEGACY_EDITOR_BUTTON: 'ff-pymt-5318-hide-add-legacy-editor-button',
  ENABLE_CLIENTS_LINE_ITEM_FOR_WORKSPACE_PLANS:
    'ff-pymt-5411-show-clients-line-item-on-workspace-plans',
  BILLING_USE_DRUID_FOR_BANDWIDTH_RECONCILIATION:
    'ff-pymt-5574-druid-billing-reconciliation',
  BILLING_DRUID_REPORT_TO_STIGG: 'ff-pymt-5574-druid-billing-report-to-stigg',
  PNP42_SITE_PLANS: 'ff-pymt-pnp-4-2-site-plans',
  PNP42_BILLABLE_CHANGE_MIGRATION: 'ff-pymt-pnp-4-2-billable-change-migration',
  PNP42_FORCED_RENEWAL_NON_VIP: 'ff-pymt-pnp-4-2-forced-renewal-non-vip',
  PNP42_FORCED_RENEWAL_VIP: 'ff-pymt-pnp-4-2-forced-renewal-vip',
  PNP42_RENEWAL_DRY_RUN: 'ff-pymt-5526-pnp42-renewal-dry-run',
  PNP42_CARRY_FORWARD_PAYING_CUSTOMER:
    'ff-pymt-6068-carry-forward-paying-customer',
  // PYMT-6330: carries Stigg subscription metadata forward onto the migrated
  // subscription. Rolled out ON — create the Statsig gate at 100% so carry-forward
  // is the steady state; flip to 0% to fall back to writing only
  // previousStiggSubscriptionId. Not in `defaultFalseFlags.ts`, so it defaults ON
  // in CI (matching prod steady state). The executor counts every flag-off
  // provision under `pnp42.migration.metadata_carry_forward_skipped`, so a gate
  // that was never created is visible in Datadog rather than silently inert.
  PNP42_CARRY_FORWARD_SUBSCRIPTION_METADATA:
    'ff-pymt-6330-carry-forward-subscription-metadata',
  LEGACY_EDITOR_MIGRATION_PROMO_CONVERSION:
    'ff-pymt-5593-legacy-editor-migration-promo-conversion',
  PRESERVE_ADDONS_ON_DOWNGRADE:
    'ff-pymt-5620-preserve-addons-on-workspace-downgrade',
  WORKSPACE_LIMITED_SEAT_ACCEPT_NOTIFICATION:
    'ff-pymt-workspace-limited-seat-accept-notification',
  IHT_FORMER_LEGACY_EDITOR_BADGE: 'ff-pymt-5761-iht-former-legacy-editor-badge',
  LEGACY_EDITOR_REINVITE_DIRECT_ACCEPT_URL:
    'ff-pymt-5713-reinvite-direct-accept-url',
  LEGACY_EDITOR_EDIT_PATH_REDIRECT:
    'ff-sub-549-legacy-editor-edit-path-redirect',
  SKIP_ADDON_PRESERVATION_ON_INVOLUNTARY_CANCEL:
    'ff-pymt-5715-skip-addon-preservation-on-involuntary-cancel',
  // Gates the WAF routing surface end-to-end: server WAF routes 404 when
  // off (see `restrictWafRouting` in entrypoints/server/routes/api/sites/waf.ts),
  // and the dashboard hides the Security menu item / page. Tracked in PYMT-5746.
  ENABLE_WAF_ROUTING: 'ff-pymt-5746-enable-waf-routing',
  // Gates the non-mutating preview and tested-apply workflow for WAF rule
  // changes. When off, WAF writes retain the historical direct-apply path.
  LIF_69_WAF_TEST_GATE: 'ff-lif-69-waf-test-gate',
  // Gates WAF staging-to-production promotion availability.
  LIF_362_WAF_STAGING_PROMOTION: 'ff-lif-362-waf-staging-promotion',
  // Gates read-only MCP access to WAF configuration and WAF-focused usage
  // analysis. Tracked in PYMT-5784.
  WAF_MCP: 'ff-pymt-5784-waf-mcp',
  FULL_SEAT_LIMITED_ROLE_FALLBACK:
    'ff-pymt-5774-full-seat-limited-role-fallback',
  // Selects which code path findBillingSubscriptionsForSites uses to apply
  // enterprise plan-name overrides: the shared updateSubscriptionsWithEnterprisePlanNames
  // helper (on) vs the legacy inline loop (off). Behavior is equivalent either way —
  // both override under BILLING_ENABLE_ENTERPRISE_SITE_NAMES — so this is a refactor
  // kill switch, not a feature gate. Tracked in PYMT-5913.
  BILLING_GENERALIZE_ENTERPRISE_PLAN_NAMES:
    'ff-pymt-5913-generalize-enterprise-plan-names',
  // When on, removes workspaceBilling.read/manage from limited-seat workspace
  // members (member/client role with a Marketer or Content Editor default site
  // role), hiding the workspace Billing/Usage nav. Clients who pay for or are
  // invited to pay for a site keep it, since that site is surfaced through the
  // workspace billing view.
  HIDE_BILLING_FROM_WORKSPACE_MEMBERS_PYMT_5911:
    'ff-pymt-5911-hide-billing-pages-from-workspace-members',
  // When on, an agency-workspace client no longer receives the workspaceBilling
  // read carve-out, so they can't open the member workspace billing page.
  SCOPE_CLIENT_INVOICES: 'ff-pymt-5973-scope-client-invoices',
  // When on, client-seat invites that omit canPublish (e.g. Enterprise/Ent-Agency
  // workspaces where the dashboard hides the toggle) defer to the role default
  // instead of forcing publish=false. Tracked in PYMT-5819.
  RESPECT_ROLE_DEFAULT_PUBLISH_FOR_CLIENT_INVITE:
    'ff-pymt-5819-role-default-publish',
  // Gates migrating `SiteClient` documents from source to destination during a
  // within-workspace hosting transfer. When off, only the legacy `SiteRoleMap`
  // follows the subscription, leaving paying clients unable to manage the plan
  // on the destination site (siteBilling permissions resolve to false) and the
  // SP's Site Access tab no longer listing them. Tracked in PYMT-5878.
  SYNC_SITE_CLIENTS_ON_TRANSFER: 'ff-pymt-5878-sync-site-clients-on-transfer',
  SP_CLIENT_AI_CREDIT_USAGE: 'ff-pymt-5955-sp-client-ai-credit-usage',
  // When on, removing a client from their LAST site client seat also tears down
  // the workspace-level client membership (WorkspaceClient + workspace `client`
  // role map), so the client no longer lingers as a phantom workspace member that
  // cannot leave and the SP cannot remove. Forward fix only — see PYMT-5954.
  CLEANUP_ORPHANED_WORKSPACE_CLIENT:
    'ff-pymt-5954-cleanup-orphaned-workspace-client',
  // Umbrella kill-switch for the Stunning → Knock billing-email migration.
  // Gates every Knock-triggered billing lifecycle email (dunning on charge
  // failure, pre-dunning card-expiry, auto-cancellation, refund, manual
  // dunning); Stunning templates are disabled per email type during cutover.
  // OFF path is a clean no-op (Stunning keeps sending). Receipt has its own
  // dedicated flag below so its cutover can be sequenced independently.
  // Tracked in PYMT-6144.
  STUNNING_DEPRECATION: 'ff-pymt-6144-stunning-deprecation',
  // Kill-switch for the Knock receipt sender (LIF-186), split out from
  // STUNNING_DEPRECATION so receipt cutover doesn't happen automatically
  // whenever that umbrella flag rolls out for the other four email types.
  BILLING_RECEIPT_CUTOVER: 'ff-lif-186-billing-receipt',
  // Kill-switch for the getEntitlements service-outage fallback. When ON, a
  // failed Stigg getAllEntitlements call degrades to the registered
  // entitlement fallbacks + legacy capabilities (Dashboard stays up); when
  // OFF, the failure propagates and the request 5xxes (pre-fix incident
  // behavior). Keep ON in steady state — OFF exists only to revert the
  // fallback path without a deploy.
  ENTITLEMENTS_SERVICE_FALLBACK: 'ff-pymt-6088-entitlements-provider-fallback',
  // Kill-switch for SUB-673. Despite the name, it covers every Stigg call, not
  // only reads. ON: StiggBillingService.getStiggClient() returns the SDK client
  // without awaiting waitForInitialization(), so a process whose SDK init
  // failed still serves Redis-cached entitlements and its writes recover once
  // Stigg does, and an SDK-served fallback (cache miss while Stigg or Redis is
  // unavailable) is raised as a 503 so callers degrade to legacy capabilities.
  // OFF: every Stigg call awaits initialization and rethrows its failure
  // (pre-SUB-673 behavior). Keep ON in steady state.
  STIGG_READS_WITHOUT_INIT_WAIT: 'ff-sub-673-stigg-reads-without-init-wait',
  // Kill-switch for retrying SUB-964 overage-notification handoff failures.
  // ON: Stigg webhooks return 500 and sandbox jobs retry when Knock cannot
  // accept a notification. OFF: failures are acknowledged as before SUB-964.
  STIGG_OVERAGE_NOTIFICATION_RETRIES:
    'ff-sub-964-stigg-overage-notification-retries',
  // Resolves a site's owner from `Site.workspace` before probing Person and
  // Organization, so workspace-owned sites skip two collection scans. Buckets on
  // the `siteId` custom ID for a deterministic per-site ramp. OFF keeps the
  // legacy Person → Organization → Workspace order. Keep the Statsig gate at 0%
  // until the dual-ownership audit and billing/auth/publishing sign-offs clear.
  // Tracked in SUB-65.
  WORKSPACE_FIRST_SITE_OWNER: 'ff-sub-65-workspace-first-site-owner',
  /**
   * Hides billing details from GET /api/sites/:site/plans (SUB-156).
   *
   * ON: callers without billing access to the site (such as the Reviewer
   * role) no longer get Stripe or partner IDs, and no caller gets
   * `latestInvoice`. OFF: every caller gets the full response, as before.
   *
   * This is a kill switch. Keep it ON, and turn it OFF only if something
   * breaks because it needed one of the hidden fields.
   *
   * Statsig returns OFF for a gate that is missing or at 0%, so production
   * hides nothing until this gate is ramped. Tests treat it as ON because it
   * is not listed in `defaultFalseFlags.ts`.
   */
  WITHHOLD_SITE_PLAN_BILLING_DETAIL: 'ff-sub-156-withhold-billing-detail',
  ENTERPRISE_SELF_PROVISIONING_SITE_SETTINGS_TAB:
    'ff-sub-440-ent-self-prov-site-settings-tab',
  // Gate for SUB-534's POST /v2/sites/:site_id/plan.
  ENTERPRISE_SITE_UPGRADE_API: 'ff-sub-534-enterprise-site-upgrade-api',
  // Gate for SUB-534's PATCH /v2/sites/:site_id/addons.
  ENTERPRISE_ADDON_UPGRADE_API: 'ff-sub-534-enterprise-addon-upgrade-api',
  ENTERPRISE_SIMPLE_SITE_CANCELLATION_API:
    'ff-sub-611-enterprise-simple-site-cancellation-api',
  // SUB-440: allows decreasing or removing add-ons. OFF keeps today's rejection.
  ENTERPRISE_ADDON_DOWNGRADE_VALIDATION:
    'ff-sub-440-ent-self-prov-addon-downgrade-validation',
  // Gate for SUB-595's `addons` on GET /v2/sites/:site_id/plan and site_plans:read.
  ENTERPRISE_SITE_PLAN_ADDONS_READ:
    'ff-sub-595-enterprise-site-plan-addons-read',
  // SUB-589: routes duplicateSites.ts through SiteProvisioningStrategy
  // (reserveSlot/provisionSubscription/verifyAndMaybeDowngradeReservedFeature/
  // releaseSlot) instead of canWorkspaceCreateMoreSites/reportUnhostedSiteUsage/
  // provisionAndReportSiteUsage, and adds post-restore subscription
  // verification. OFF preserves the exact pre-SUB-589 behavior.
  DUPLICATE_SITE_PROVISIONING_STRATEGY:
    'ff-sub-589-duplicate-site-provisioning-strategy',
};
